import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Megaphone } from "lucide-react";

/** Shows pending announcements once per user until they acknowledge them. */
export const AnnouncementModal = () => {
  const [queue, setQueue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/announcements/pending").then((r) => setQueue(r.data || [])).catch(() => {});
  }, []);

  const current = queue[0];
  if (!current) return null;

  const ack = async (go) => {
    await api.post(`/announcements/${current.id}/read`).catch(() => {});
    setQueue((q) => q.slice(1));
    if (!go || !current.link) return;
    // Un enlace externo (https://...) se abre en otra pestaña; uno interno
    // (/dashboard/...) navega dentro de la plataforma.
    if (/^https?:\/\//i.test(current.link)) window.open(current.link, "_blank", "noopener");
    else navigate(current.link);
  };

  return (
    <Dialog open onOpenChange={() => ack(false)}>
      <DialogContent
        className="max-w-md max-h-[85dvh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="announcement-modal" aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <div className="flex items-center gap-2 text-brand">
            <Megaphone className="h-5 w-5" />
            <span className="text-[11px] font-bold uppercase tracking-[0.14em]">Novedad</span>
          </div>
          <DialogTitle className="text-left pt-1">{current.title}</DialogTitle>
        </DialogHeader>
        {/* El anuncio puede ser largo: el cuerpo scrollea y el botón se queda
            siempre a la vista, sin que nadie tenga que achicar la pantalla. */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3" data-testid="announcement-scroll">
          {current.image_url && (
            <img
              src={current.image_url.startsWith("http") ? current.image_url : `${API_BASE}${current.image_url}`}
              alt=""
              data-testid="announcement-image"
              className="w-full max-h-56 object-cover rounded-xl border border-slate-200"
            />
          )}
          <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid="announcement-body">
            {current.body}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-white shrink-0">
          {/* Con enlace, el botón de acción es suficiente: tocarlo ya cuenta como
              leído. Sin enlace, "Entendido" es la única forma de confirmar. */}
          {current.link ? (
            <>
              <Button variant="ghost" onClick={() => ack(false)} data-testid="announcement-later">
                Después
              </Button>
              <Button onClick={() => ack(true)} data-testid="announcement-cta">
                {current.cta_label || "Ver ahora"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => ack(false)} data-testid="announcement-ack">Entendido</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementModal;
